import { Analytics } from '@cobliteam/analytics';
import type { Driver } from '@cobliteam/api-lib';
import { useDriverFaceCluster, useFaceImageUploadPool } from '@cobliteam/hooks';
import {
  Banner,
  BannerContent,
  BannerIcon,
  BannerText,
  Divider,
  FileUpload,
  FileUploadDescription,
  FileUploadDropZone,
  FileUploadPreview,
  Typography,
} from '@cobliteam/ui/components';
import type { FileMetadata as FileUploadMetadata } from '@cobliteam/ui/components/FileUpload';
import Icon from '@cobliteam/ui/icons';
import { getPluralWithCount } from '@cobliteam/utils/pluralize';
import { AnimatePresence, motion } from 'motion/react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import DriverFaceIdHeader from './components/DriverFaceIdHeader';

const MAX_SIZE_MB = 5;
const UPLOAD_IMAGES_LIMIT = 10;
const AI_IMAGES_LIMIT = 20;

const TOAST_ANIMATION = {
  hidden: { opacity: 0, y: 5 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 5 },
};

type FileMetadata = FileUploadMetadata & {
  faceImageUrl?: string;
};

const idsEquals = <T extends { id?: string }>(a: T, b: T) => a?.id === b?.id;

/** ![](https://mathmonks.com/wp-content/uploads/2024/05/Difference-in-Sets.jpg) */
const arrayDifference = <T extends { id?: string }>(a: T[], b: T[], equalsComparison = idsEquals): T[] => a.filter((aEl) => !b.some((bEl) => equalsComparison(aEl, bEl)));

export interface DriverImagesRef {
  handleSubmit: () => void;
  hasChanges: () => boolean;
}

interface AssignmentRequest {
  url: string;
  useAsProfileImage?: boolean;
}

export interface StagedFaceImageUpdate {
  changedAssignments: AssignmentRequest[];
  removedAssignments: AssignmentRequest[];
  profileImageId?: string;
  aiFiles: FileMetadata[];
  uploadFiles: FileMetadata[];
  previewUrls: string[];
}

interface DriverImagesUploadProps {
  driver: Driver;
  // Staged update changes are Confirmed but not Saved
  stagedImagesUpdate?: StagedFaceImageUpdate | null;
  setCanSubmit: (canSubmit: boolean) => void;
  onFinish: (imageUpdates: StagedFaceImageUpdate) => void;
}

const getFaceImageUrl = (url: string) => url.replace('_cropped', '');

const DriverImagesUpload = forwardRef<DriverImagesRef, DriverImagesUploadProps>(({ driver, stagedImagesUpdate, setCanSubmit, onFinish }: DriverImagesUploadProps, ref) => {
  const { getDriverFaceCluster } = useDriverFaceCluster();
  const { data: faceId } = getDriverFaceCluster(driver.id, { enabled: true });

  const { handleDone, uploadImages } = useFaceImageUploadPool({ maxConcurrency: 5 });

  const faceIdImages: FileMetadata[] = (faceId?.sampleImageUrls || []).map(({ imageUrl }) => ({
    url: imageUrl,
    id: imageUrl,
    name: imageUrl,
    size: 0,
    type: '',
    faceImageUrl: getFaceImageUrl(imageUrl),
  }));

  const savedAiFiles = faceIdImages.filter((image) => !image.url.includes('upload-'));
  const savedUploadFiles = faceIdImages.filter((image) => image.url.includes('upload-'));
  const savedProfileImageId = faceId?.profileImageUrl.imageUrl;

  const initialAiFiles = stagedImagesUpdate?.aiFiles || savedAiFiles;
  const initialUploadFiles = stagedImagesUpdate?.uploadFiles || savedUploadFiles;
  const initialProfileImageId = stagedImagesUpdate?.profileImageId || savedProfileImageId;

  const uploadedIds = useRef(new Set(initialUploadFiles.map((it) => it.id)));

  const [aiFiles, setAiFiles] = useState<FileMetadata[]>(initialAiFiles);
  const [uploadFiles, setUploadFiles] = useState<FileMetadata[]>(initialUploadFiles);
  const [profileImageId, setProfileImageId] = useState(initialProfileImageId);

  const [errorCount, setErrorCount] = useState(0);

  const onUploadDone = (id: string, result: FileMetadata) => {
    setUploadFiles((files) =>
      files.map((file) => {
        const found = file.id === id;
        return found ? result : file;
      })
    );

    if (result.error) {
      setErrorCount((count) => count + 1);
    }
  };

  handleDone(onUploadDone);

  /** Clears error count after 5s */
  useEffect(() => {
    if (errorCount === 0) return;

    const timeoutId = setTimeout(() => {
      setErrorCount(0);
    }, 10000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [errorCount]);

  /** Ensures that new images are uploaded */
  const syncImagesUpload = useCallback(async (toSyncFiles: FileMetadata[]) => {
    const toUploadFiles = toSyncFiles.filter((file) => !uploadedIds.current.has(file.id));
    if (toUploadFiles.length === 0) return;

    for (const file of toUploadFiles) {
      uploadedIds.current.add(file.id);
    }

    uploadImages(toUploadFiles);

    setUploadFiles((files) =>
      files.map((file) => {
        const loading = toUploadFiles.some((f) => idsEquals(f, file));

        return {
          ...file,
          loading: file.loading || loading,
        };
      })
    );
  }, []);

  const onUploadFilesChanged = useCallback(
    async (updatedFiles: FileMetadata[]) => {
      const hasError = updatedFiles.some((file) => file.error);
      const hasLoading = updatedFiles.some((file) => file.loading);

      setCanSubmit(!(hasError || hasLoading));
      setUploadFiles(updatedFiles);

      const newFiles = arrayDifference(updatedFiles, faceIdImages);
      syncImagesUpload(newFiles);
    },
    [syncImagesUpload, setUploadFiles]
  );

  const handleSubmit = () => {
    const profileImage = uploadFiles.find((it) => it.id === profileImageId);

    const changedImages = arrayDifference(uploadFiles, savedUploadFiles);

    const changedAssignments: AssignmentRequest[] = changedImages.map((it) => ({
      url: it.faceImageUrl!,
    }));

    // Profile image changed
    if (profileImage && profileImage.id !== savedProfileImageId) {
      const assignment = changedAssignments.find((it) => it.url === profileImage.faceImageUrl);

      if (assignment) {
        assignment.useAsProfileImage = true;
      } else {
        changedAssignments.push({
          url: profileImage.faceImageUrl!,
          useAsProfileImage: true,
        });
      }

      Analytics.track('Driver Drawer - Profile Image - Change');
    }

    const removedUploadFiles = arrayDifference(savedUploadFiles, uploadFiles);
    const removedAiFiles = arrayDifference(savedAiFiles, aiFiles);

    const removedAssignments: AssignmentRequest[] = removedAiFiles.concat(removedUploadFiles).map((it) => ({
      url: it.faceImageUrl!,
    }));

    const previewSamplesUrl = uploadFiles
      .concat(aiFiles)
      .filter((it) => it.id !== profileImage?.id)
      .map((it) => it.url);

    const previewUrls = profileImage ? [profileImage.url, ...previewSamplesUrl] : previewSamplesUrl;

    const updateImagesData: StagedFaceImageUpdate = {
      removedAssignments,
      changedAssignments,
      aiFiles,
      uploadFiles,
      profileImageId,
      previewUrls,
    };

    Analytics.track('Driver Drawer - Images - Change', {
      deletedAiImagesCount: removedAiFiles.length,
      deletedUploadImagesCount: removedUploadFiles.length,
      uploadedImagesCount: changedImages.length,
    });

    onFinish(updateImagesData);
  };

  const hasChanges = () => {
    const changedImages = arrayDifference(uploadFiles, savedUploadFiles);
    const removedUploadFiles = arrayDifference(savedUploadFiles, uploadFiles);
    const removedAiFiles = arrayDifference(savedAiFiles, aiFiles);
    const profileImageChanged = profileImageId !== undefined && profileImageId !== savedProfileImageId;

    return changedImages.length > 0 || removedUploadFiles.length > 0 || removedAiFiles.length > 0 || profileImageChanged;
  };

  useImperativeHandle(ref, () => {
    return {
      handleSubmit,
      hasChanges,
    };
  }, [handleSubmit, hasChanges]);

  return (
    <div className='relative flex flex-col gap-3'>
      <Typography variant='text-md' color='secondary' className='mb-2'>
        Nos envie imagens do motorista para o cadastro e aprimoramento da detecção de identificação facial.
      </Typography>
      <DriverFaceIdHeader filesCount={uploadFiles.length} filesMax={UPLOAD_IMAGES_LIMIT} />
      {/* Images managed by the user */}
      <FileUpload
        fileAcceptTypes={{
          'image/jpeg': ['.jpg', '.jpeg'],
          'image/png': ['.png'],
        }}
        maxFiles={UPLOAD_IMAGES_LIMIT}
        maxSizeMB={MAX_SIZE_MB}
        files={uploadFiles}
        mainFileId={profileImageId}
        onFilesChange={onUploadFilesChanged}
        onMainFileChange={setProfileImageId}
        showMainFile
        multiple
      >
        <FileUploadDropZone />
        <FileUploadPreview />
        <FileUploadDescription />
      </FileUpload>
      {/* Images identified by Cobli */}
      {aiFiles.length > 0 && (
        <>
          <Divider className='my-2' />
          <div className='flex items-center gap-4'>
            <Icon size='lg' color='gray' name='video' />
            <Typography variant='text-md-semibold'>
              Fotos captadas automaticamente pela Cobli Cam ({aiFiles.length}/{AI_IMAGES_LIMIT})
            </Typography>
          </div>
          <FileUpload files={aiFiles} onFilesChange={setAiFiles}>
            <FileUploadPreview />
          </FileUpload>
        </>
      )}
      <AnimatePresence>
        {errorCount > 0 && (
          <motion.div className='absolute top-0 w-full' initial={'hidden'} animate={'visible'} exit={'exit'} variants={TOAST_ANIMATION} transition={{ duration: 0.2 }}>
            <Banner
              color='danger'
              onClose={() => {
                setErrorCount(0);
              }}
            >
              <BannerIcon icon={<Icon name='file-x' color='danger' />} />
              <BannerContent>
                <BannerText className='font-bold'>
                  {errorCount} {getPluralWithCount('erro', errorCount)} {getPluralWithCount('identificado', errorCount)}
                </BannerText>
                <BannerText>Confira mais detalhes nas imagens abaixo</BannerText>
              </BannerContent>
            </Banner>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

export default DriverImagesUpload;
